import * as React from 'react';

import { IInvitationModel, IInvitationRegistrationModel } from '../../interfaces/IUser';

import { ApplicationHelpType } from '../../utilities/Constants';
import CheckmateApiClient from '../../services/apiClient';
import { DisplayMessages } from '../../utilities/DisplayMessages';
import { Help } from '../../components/shared/Help';
import { IValidation } from '../../interfaces/IError';
import { Loader } from '../../components/shared/Loader';
import { LoadingButton } from '../../shared/buttons/LoadingButton';
import { LocalRoutes } from '../../utilities/LocalRoutes';
import { Navigate } from 'react-router-dom';
import ValidateUtils from '../../shared/validations';
import { showSuccessToast } from '../../shared/ToastUtils';

const _apiClient = new CheckmateApiClient();

interface IRegisterAccountProps {
    guid: string;
}

interface IRegisterAccountState {
    userInvitation: IInvitationModel;
    invitationRegistration: IInvitationRegistrationModel;
    isCreatingUser: boolean;
    isCreateSuccess: boolean;
    validation: IValidation;
    redirect: boolean;
    redirectTo: string;
}

export class RegisterWithInvitation extends React.Component<
    IRegisterAccountProps,
    IRegisterAccountState
> {
    constructor(props: any) {
        super(props);

        this.state = {
            userInvitation: { organizationRoles: [] },
            invitationRegistration: {},
            isCreatingUser: false,
            isCreateSuccess: false,
            validation: {},
            redirect: false,
            redirectTo: '/',
        };
    }

    componentDidMount() {
        this.loadUserInvitation(this.props.guid);
    }

    loadUserInvitation = async (guid: string) => {
        try {
            const response = await _apiClient.getUserInvitation(guid);
            if (response.httpResponse.status == 401) {
                window.location.reload();
                return;
            }
            if (response.errorMessage) {
                this.setState({
                    validation: ValidateUtils.parseErrors(response.errors, response.errorMessage),
                });
                return;
            }
            if (response.payload) {
                this.setState({
                    userInvitation: response.payload,
                    invitationRegistration: {
                        username: response.payload.email,
                        invitationGuid: guid,
                    },
                });
            }
            return true;
        } catch (_error) {
            return false;
        }
    };

    validatePassword = () => {
        let password = this.state.invitationRegistration.password;
        let confirmPassword = this.state.invitationRegistration.confirmPassword;
        let validation = this.state.validation;

        if (!password || !confirmPassword) {
            validation.password = [DisplayMessages.PasswordRequired];
            this.setState({ validation: validation });
            return;
        }

        if (password !== confirmPassword) {
            validation.password = [DisplayMessages.PasswordMatch];
            this.setState({ validation: validation });
            return;
        }

        // if (!password!.match(/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])[0-9a-zA-Z]{8,}$/)) {
        //     validation.password = [DisplayMessages.PasswordCriteria];
        //     this.setState({ validation: validation });
        //     return;
        // }

        this.createUserAccount();
    };

    createUserAccount = async () => {
        let userInformation = this.state.invitationRegistration;

        this.setState({ isCreatingUser: true });
        const response = await _apiClient.createUserAccount(userInformation);
        if (response.errorMessage) {
            this.setState({
                isCreateSuccess: false,
                validation: ValidateUtils.parseErrors(response.errors, response.errorMessage),
            });
        } else {
            this.setState({ isCreateSuccess: true });
            showSuccessToast(<span>User Account created successfully.</span>);
            setTimeout(() => {
                this.setState({ redirect: true });
            }, 5000);
        }

        this.setState({ isCreatingUser: false });
    };

    changeValue = (
        event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>
    ) => {
        const name = event.target.name;
        const value = event.target.value;

        this.setChangedValue(name, value);
    };

    setChangedValue(name: string, value: any) {
        let invitationRegistration = this.state.invitationRegistration;
        let validation = this.state.validation;

        switch (name) {
            case 'password':
                invitationRegistration.password = value;
                validation.password = [];
                break;
            case 'confirmPassword':
                invitationRegistration.confirmPassword = value;
                validation.confirmPassword = [];
                break;
            default:
                break;
        }
        this.setState({
            invitationRegistration: invitationRegistration,
            validation: validation,
        });
    }

    handleConfirm = () => {
        this.setState({ redirect: true });
    };

    handleCancel = () => {
        this.setState({
            redirect: true,
            redirectTo: LocalRoutes.AcceptInvitation.replace(':guid', this.props.guid),
        });
    };

    render() {
        if (this.state.redirect) {
            return <Navigate to={this.state.redirectTo} />;
        }

        const disableForm = this.state.isCreatingUser || this.state.isCreateSuccess;

        return (
            <div>
                {this.state.isCreatingUser && <Loader />}
                <h1>
                    Create Account
                    {!this.state.isCreateSuccess && (
                        <span className="float-end">
                            <LoadingButton  
                                disabled={this.state.isCreatingUser}
                                buttonCssClass="btn btn-green horizontal-margin"
                                onClick={this.validatePassword}
                                loading={this.state.isCreatingUser}
                                loadingText='Creating User'
                                text="Create"
                            />
                            <input
                                disabled={this.state.isCreatingUser}
                                type="button"
                                className="btn btn-default"
                                onClick={this.handleCancel}
                                value="Cancel"
                            />
                        </span>
                    )}
                </h1>
                <h5 className="vertical-padding-lg text-bold">{DisplayMessages.CreateAccount}</h5>
                <span className="text-danger ">{this.state.validation.model}</span>
                <div className="row mb-2">
                    <div className="col-sm-2">
                        <label className="text-gray">Username/Email</label>
                    </div>
                    <div className="col-sm-5">
                        <input
                            disabled
                            className="form-control"
                            type="text"
                            name="email"
                            value={this.state.userInvitation.email || ''}
                        />
                    </div>
                </div>
                <div className="row mb-2">
                    <div className="col-sm-2">
                        <label className="text-gray">Password</label>
                        <Help
                            type={ApplicationHelpType.Info}
                            title="Password Requirements"
                            helpText="* Minimum of 8 characters\n* Must Contain at least 1 lowercase letter\n* Must Contain at least 1 capital letter\n* Must Contain at least 1 number\n"
                        />
                    </div>
                    <div className="col-sm-5">
                        <input
                            className="form-control"
                            disabled={disableForm}
                            style={{ display: 'inline-block' }}
                            type="password"
                            name="password"
                            value={this.state.invitationRegistration.password || ''}
                            onChange={this.changeValue}
                        />
                        <span className="text-danger ms-2">{this.state.validation.password}</span>
                    </div>
                </div>
                <div className="row mb-2">
                    <div className="col-sm-2">
                        <label className="text-gray">Confirm Password*</label>
                    </div>
                    <div className="col-sm-5">
                        <input
                            className="form-control"
                            disabled={disableForm}
                            style={{ display: 'inline-block' }}
                            type="password"
                            name="confirmPassword"
                            value={this.state.invitationRegistration.confirmPassword || ''}
                            onChange={this.changeValue}
                        />
                        <span className="text-danger ms-2">
                            {this.state.validation.confirmPassword}
                        </span>
                    </div>
                </div>
            </div>
        );
    }
}
